import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Header = ({ onOpenContactForm }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { label: 'Home', id: 'home' },
    { label: 'Services', id: 'services' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'Testimonials', id: 'testimonials' },
    { label: 'FAQ', id: 'faq' },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-[#09090B]/90 backdrop-blur-2xl border-b border-white/[0.06] shadow-2xl shadow-black/20'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button
            data-testid="header-logo"
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5 cursor-pointer group"
          >
            <div className="w-9 h-9 rounded-lg btn-gradient flex items-center justify-center">
              <span className="text-white font-heading font-bold text-sm">TP</span>
            </div>
            <div className="leading-tight">
              <span className="font-heading text-[15px] font-semibold text-white block">Trademark Protection</span>
              <span className="text-gradient text-xs font-semibold">For Logos</span>
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => scrollToSection(item.id)}
                className="text-sm font-medium text-[#A1A1AA] hover:text-white px-4 py-2 rounded-lg hover:bg-white/[0.05] transition-all duration-200 nav-hover"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:+18005551234" className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white transition-colors">
              <Phone className="w-3.5 h-3.5" />
              (800) 555-1234
            </a>
            <Button
              data-testid="header-cta"
              onClick={onOpenContactForm}
              className="btn-gradient text-white font-semibold text-sm px-5 py-2.5 rounded-lg border-0"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Dropdown */}
        {isMobileMenuOpen && (
          <div data-testid="mobile-menu" className="lg:hidden mt-4 pb-4 border-t border-white/[0.06] pt-4">
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-sm font-medium text-[#A1A1AA] hover:text-white text-left px-3 py-2.5 rounded-lg hover:bg-white/[0.05] transition-all"
                >
                  {item.label}
                </button>
              ))}
              <div className="pt-3 mt-2 border-t border-white/[0.06] flex flex-col gap-3">
                <a href="tel:+18005551234" className="flex items-center gap-2 text-[#A1A1AA] text-sm px-3">
                  <Phone className="w-4 h-4" /> (800) 555-1234
                </a>
                <Button
                  onClick={onOpenContactForm}
                  className="w-full btn-gradient text-white font-semibold text-sm rounded-lg border-0"
                >
                  Get Started
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
